import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const GoogleCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);

  const handleSocialLoginSuccess = useCallback(
    async (provider, userData) => {
      try {
        // Mevcut social-login endpoint'ini kullan (backend'de zaten var)
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/social-login`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              provider,
              ...userData,
            }),
          }
        );

        const data = await response.json();

        if (response.ok && data.user) {
          // Kullanıcı bilgilerini kaydet
          sessionStorage.setItem("user_id", data.user.id);
          localStorage.setItem("userId", data.user.id);
          localStorage.setItem("user", JSON.stringify(data.user));

          Swal.fire({
            icon: "success",
            title: t("LoginSuccessful"),
            text: t("WelcomeUser", { name: data.user.name }),
            confirmButtonText: t("OK"),
          }).then(() => {
            window.location.href = `/profile/${data.user.id}`;
          });
        } else {
          throw new Error(data.error || "Login failed");
        }
      } catch (error) {
        console.error("Social login API error:", error);
        Swal.fire({
          icon: "error",
          title: t("AlertErrorTitle"),
          text: error.message || t("APIerrortext"),
          confirmButtonText: t("OK"),
        }).then(() => {
          navigate("/userlogin");
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [t, navigate]
  );

  useEffect(() => {
    const handleGoogleCallback = async () => {
      // Implicit flow returns data in URL hash fragment (#)
      // Example: #access_token=xxx&token_type=Bearer&expires_in=3600&state=xxx
      const hash = location.hash.substring(1); // Remove the # symbol
      const params = new URLSearchParams(hash);

      const accessToken = params.get("access_token");
      const state = params.get("state");
      const error = params.get("error");

      // Check for errors from Google
      if (error) {
        console.error("Google OAuth error:", error);
        Swal.fire({
          icon: "error",
          title: t("AlertErrorTitle"),
          text: t("socialLogin.googleError") || "Google login failed. Please try again.",
          confirmButtonText: t("OK"),
        }).then(() => {
          navigate("/userlogin");
        });
        return;
      }

      // Verify state for CSRF protection
      const savedState = sessionStorage.getItem("google_oauth_state");
      if (state !== savedState) {
        console.error("State mismatch - possible CSRF attack");
        Swal.fire({
          icon: "error",
          title: t("AlertErrorTitle"),
          text: t("socialLogin.securityError") || "Security verification failed. Please try again.",
          confirmButtonText: t("OK"),
        }).then(() => {
          navigate("/userlogin");
        });
        return;
      }

      // Clear saved state
      sessionStorage.removeItem("google_oauth_state");

      if (!accessToken) {
        Swal.fire({
          icon: "error",
          title: t("AlertErrorTitle"),
          text: t("socialLogin.noTokenError") || "Access token not received.",
          confirmButtonText: t("OK"),
        }).then(() => {
          navigate("/userlogin");
        });
        return;
      }

      try {
        // Access token ile Google userinfo API'den kullanıcı bilgilerini al
        const response = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch user info from Google");
        }

        const profile = await response.json();

        // Mevcut social-login endpoint'ini kullan
        await handleSocialLoginSuccess("google", {
          id: profile.sub,
          email: profile.email,
          name: profile.name,
          picture: profile.picture,
          accessToken,
        });
      } catch (error) {
        console.error("Google callback error:", error);
        Swal.fire({
          icon: "error",
          title: t("AlertErrorTitle"),
          text: error.message || t("APIerrortext"),
          confirmButtonText: t("OK"),
        }).then(() => {
          navigate("/userlogin");
        });
        setIsProcessing(false);
      }
    };

    handleGoogleCallback();
  }, [location.hash, navigate, t, handleSocialLoginSuccess]);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-6 text-center">
          {isProcessing ? (
            <>
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h4>{t("socialLogin.processingLogin") || "Processing login..."}</h4>
              <p className="text-muted">
                {t("socialLogin.pleaseWait") || "Please wait while we complete your login."}
              </p>
            </>
          ) : (
            <p>{t("socialLogin.redirecting") || "Redirecting..."}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCallback;
